import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  TextField,
  Stack,
  FormControl,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Tooltip,
  Button,
  useMediaQuery,
  Grid
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import LoginIcon from "@mui/icons-material/Login";
import VisibilityIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOffOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import DriveEtaOutlinedIcon from "@mui/icons-material/DriveEtaOutlined";
import PreviewOutlinedIcon from "@mui/icons-material/PreviewOutlined";
import { useTheme } from "@mui/material/styles";

import { useStatus } from "../../popup/NotificationContext";
import { loginSuccess } from "../../store/authSlice";
import {
  BasicDetailsStep,
  StickyModalFooter,
  WizardModalLayout,
} from "./wizard/WizardComponents";
import {
  adminApi,
  useCreateUserMutation,
  useDeleteUserMutation,
  useEditDeviceMutation,
  useEditUserMutation,
  useGetAllDevicesQuery,
  useGetAllLiteDevicesQuery,
  useGetMyDevicesQuery,
  useGetAllUsersQuery,
  useImpersonateUserMutation,
  useRevealUserPasswordMutation,
  useResetPasswordMutation,
} from "../../redux/admin/adminApi";
import { normalizeRole, ROLES } from "../../utils/rbacHelper";

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 1.5,
    backgroundColor: "#fff",
  },
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-GB");
};

const toYmd = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const isDriverUser = (user) => {
  if (!user) return false;
  const role = normalizeRole(user?.role);
  if (role === ROLES.DRIVER) return true;
  if (role !== ROLES.VIEWER && role !== ROLES.CLIENT) return false;

  const email = String(user?.email || "").toLowerCase();
  const isDriverEmail = email.endsWith("@driver.com") || email.includes("@driver.com");
  const hasDriverFields = Boolean(user?.licenseNo || user?.licenseValidity || user?.salary || user?.address);
  return isDriverEmail || hasDriverFields;
};

const isPlainViewerUser = (user) => {
  if (!user) return false;
  const role = normalizeRole(user?.role);
  if (role !== ROLES.VIEWER) return false;
  return !isDriverUser(user);
};

const buildCsv = (rows) => {
  const headers = [
    "Id",
    "Driver Name",
    "Unique ID",
    "Mobile",
    "Address",
    "License no",
    "License Validity",
    "Salary",
  ];

  const body = rows.map((row) =>
    [
      row.idLabel,
      row.username,
      row.uniqueId,
      row.mobileNo,
      row.address,
      row.licenseNo,
      row.licenseValidityLabel,
      row.salary,
    ]
      .map((value) => `"${String(value ?? "").replace(/"/g, '""')}"`)
      .join(",")
  );
  return [headers.join(","), ...body].join("\n");
};

const EmptyState = () => (
  <Stack spacing={1} alignItems="center" sx={{ py: 10, color: "#6b7280" }}>
    <Box
      sx={{
        width: 92,
        height: 92,
        borderRadius: "50%",
        bgcolor: "#fff1f2",
        display: "grid",
        placeItems: "center",
        color: "#fb7185",
        fontSize: 34,
        userSelect: "none",
      }}
    >
      ✕
    </Box>
    <Typography sx={{ fontWeight: 700 }}>No Data Available In Table</Typography>
  </Stack>
);

export default function DriverPortalModal({ open = true, onClose, entityType = "driver" }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.auth?.user);
  const currentUserId = String(currentUser?._id || currentUser?.id || "");
  const currentUserRole = normalizeRole(currentUser?.role);
  const navigate = useNavigate();
  const { showStatus } = useStatus();
  const { data: usersPayload = [], refetch } = useGetAllUsersQuery(undefined, { skip: !open });
  const useScopedViewerDeviceSource = entityType === "viewer" && ["user", "driver", "client", "viewer"].includes(currentUserRole);
  const { data: devicesPayload = [] } = useGetAllLiteDevicesQuery(undefined, {
    skip: !open || entityType !== "viewer" || useScopedViewerDeviceSource,
  });
  const { data: fullDevicesPayload = [] } = useGetAllDevicesQuery(undefined, {
    skip: !open || entityType !== "viewer" || useScopedViewerDeviceSource,
  });
  const { data: myDevicesPayload = { data: [] } } = useGetMyDevicesQuery(
    { includeExpired: "true" },
    { skip: !open || entityType !== "viewer" || !useScopedViewerDeviceSource }
  );
  const [impersonateUser] = useImpersonateUserMutation();
  const [createUser, { isLoading: isCreating }] = useCreateUserMutation();
  const [editUser, { isLoading: isEditing }] = useEditUserMutation();
  const [editDevice] = useEditDeviceMutation();
  const [deleteUser, { isLoading: isDeleting }] = useDeleteUserMutation();
  const [revealUserPassword] = useRevealUserPasswordMutation();
  const [resetPassword, { isLoading: isResetting }] = useResetPasswordMutation();

  const [searchQuery, setSearchQuery] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const isViewerMode = entityType === "viewer";
  const entityLabel = isViewerMode ? "Viewer" : "Driver";
  const entityLabelPlural = isViewerMode ? "Viewers" : "Drivers";

  const [mode, setMode] = useState("table"); // table | create | edit
  const [editingDriver, setEditingDriver] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [revealedPasswords, setRevealedPasswords] = useState({});
  const [loadingPasswordIds, setLoadingPasswordIds] = useState(() => new Set());
  const [assignDeviceDialogUser, setAssignDeviceDialogUser] = useState(null);
  const [assignDeviceSearch, setAssignDeviceSearch] = useState("");
  const [pendingAssignIds, setPendingAssignIds] = useState(() => new Set());
  const [assignOverrides, setAssignOverrides] = useState(() => new Map());
  const [assignDeviceCache, setAssignDeviceCache] = useState(() => new Map());
  const [form, setForm] = useState({
    username: "",
    uniqueId: "",
    mobileNo: "",
    password: "",
    address: "",
    licenseNo: "",
    licenseValidity: "",
    salary: "",
  });
  const [wizardStep, setWizardStep] = useState(0);
  const [showStepErrors, setShowStepErrors] = useState(false);

  const drivers = useMemo(() => {
    const users = Array.isArray(usersPayload) ? usersPayload : usersPayload?.data || [];
    return (Array.isArray(users) ? users : [])
      .filter((u) => !u?.isDeleted)
      .filter(isViewerMode ? isPlainViewerUser : isDriverUser)
      .map((u, idx) => ({
        raw: u,
        id: u?._id || u?.id,
        idLabel: u?.id || u?._id?.slice?.(-4) || String(idx + 1),
        username: u?.username || "-",
        email: u?.email || "-",
        uniqueId: u?.uniqueId || "-",
        mobileNo: u?.mobileNo || "-",
        address: u?.address || "-",
        licenseNo: u?.licenseNo || "-",
        licenseValidity: u?.licenseValidity || "",
        licenseValidityLabel: formatDate(u?.licenseValidity),
        salary: u?.salary || "-",
      }));
  }, [isViewerMode, usersPayload]);

  const filteredDrivers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return drivers;
    return drivers.filter((row) => {
      return [row.username, row.email, row.uniqueId, row.mobileNo, row.address, row.licenseNo]
        .some((value) => String(value || "").toLowerCase().includes(query));
    });
  }, [drivers, searchQuery]);

  const safeDevices = useMemo(() => {
    const rawRows = useScopedViewerDeviceSource
      ? (
          Array.isArray(myDevicesPayload)
            ? myDevicesPayload
            : Array.isArray(myDevicesPayload?.data)
              ? myDevicesPayload.data
              : Array.isArray(myDevicesPayload?.devices)
                ? myDevicesPayload.devices
                : []
        )
      : ((Array.isArray(devicesPayload) && devicesPayload.length)
          ? devicesPayload
          : Array.isArray(fullDevicesPayload)
            ? fullDevicesPayload
            : []);

    const rows = rawRows
      .filter((device) => String(device?.status || "").toLowerCase() !== "deleted");

    const shouldRestrictViewerAssignments =
      isViewerMode && ["user", "driver", "client"].includes(currentUserRole) && !useScopedViewerDeviceSource;

    const filteredRows = shouldRestrictViewerAssignments
      ? rows.filter((device) => {
          const assignedToUserId = String(device?.assignedToUser?._id || device?.assignedToUser || "");
          const adminOwnerId = String(device?.adminId?._id || device?.adminId || "");
          const legacyAssignedIds = Array.isArray(device?.assignedToUsers) ? device.assignedToUsers.map((id) => String(id?._id || id || "")) : [];
          return (
            assignedToUserId === currentUserId ||
            adminOwnerId === currentUserId ||
            legacyAssignedIds.includes(currentUserId)
          );
        })
      : rows;

    return filteredRows.sort((left, right) =>
      String(left?.name || "").localeCompare(String(right?.name || ""))
    );
  }, [currentUserId, currentUserRole, devicesPayload, fullDevicesPayload, isViewerMode, myDevicesPayload, useScopedViewerDeviceSource]);

  useEffect(() => {
    const viewerId = String(assignDeviceDialogUser?.id || assignDeviceDialogUser?._id || "");
    if (!viewerId) {
      setAssignDeviceCache(new Map());
      return;
    }

    setAssignDeviceCache((prev) => {
      const next = new Map(prev);
      safeDevices.forEach((device) => {
        const deviceId = String(device?._id || device?.id || "");
        if (!deviceId) return;
        next.set(deviceId, {
          ...(next.get(deviceId) || {}),
          ...device,
        });
      });
      return next;
    });
  }, [assignDeviceDialogUser, safeDevices]);

  const assignableDevices = useMemo(() => {
    const viewerId = String(assignDeviceDialogUser?.id || assignDeviceDialogUser?._id || "");
    const query = String(assignDeviceSearch || "").trim().toLowerCase();

    const rows = Array.from(assignDeviceCache.values()).map((device) => {
      const deviceId = String(device?._id || device?.id || "");
      const assignedViewers = Array.isArray(device?.assignedToViewers) ? device.assignedToViewers : [];
      const baseChecked = assignedViewers.some((viewer) => String(viewer?._id || viewer) === viewerId);
      const overrideChecked = assignOverrides.get(deviceId);
      const checked = typeof overrideChecked === "boolean" ? overrideChecked : baseChecked;

      return {
        id: deviceId,
        name: device?.name || device?.vehicleNumber || device?.imei || "-",
        imei: device?.imei || "-",
        status: String(device?.liveData?.vehicleStatus || device?.status || "No_data"),
        lastUpdate: device?.lastDataReceivedAt
          ? new Date(device.lastDataReceivedAt).toLocaleString("en-GB", { hour12: true }).toUpperCase()
          : "",
        simNo: device?.msdn || "-",
        model: device?.model || "-",
        icon: device?.vehicleType || device?.deviceIcon || "Default",
        assignedToViewers: assignedViewers.map((viewer) => String(viewer?._id || viewer)),
        checked,
      };
    });

    rows.sort((left, right) => {
      if (left.checked !== right.checked) return Number(right.checked) - Number(left.checked);
      return String(left.name || "").localeCompare(String(right.name || ""));
    });

    if (!query) return rows;

    return rows.filter((row) =>
      [row.name, row.imei, row.status, row.lastUpdate, row.simNo, row.model, row.icon]
        .some((value) => String(value || "").toLowerCase().includes(query))
    );
  }, [assignDeviceCache, assignDeviceDialogUser, assignDeviceSearch, assignOverrides]);

  const assignDeviceDebug = useMemo(
    () => ({
      liteCount: Array.isArray(devicesPayload) ? devicesPayload.length : 0,
      fullCount: Array.isArray(fullDevicesPayload) ? fullDevicesPayload.length : 0,
      myDevicesCount: Array.isArray(myDevicesPayload)
        ? myDevicesPayload.length
        : Array.isArray(myDevicesPayload?.data)
          ? myDevicesPayload.data.length
          : Array.isArray(myDevicesPayload?.devices)
            ? myDevicesPayload.devices.length
            : 0,
      safeCount: Array.isArray(safeDevices) ? safeDevices.length : 0,
      cacheCount: assignDeviceCache.size,
      visibleCount: Array.isArray(assignableDevices) ? assignableDevices.length : 0,
      query: String(assignDeviceSearch || "").trim(),
      targetUserId: String(assignDeviceDialogUser?.id || assignDeviceDialogUser?._id || ""),
    }),
    [assignDeviceCache.size, assignDeviceDialogUser, assignDeviceSearch, assignableDevices, devicesPayload, fullDevicesPayload, myDevicesPayload, safeDevices]
  );

  const totalPages = Math.max(1, Math.ceil(filteredDrivers.length / rowsPerPage));
  const canRevealPasswords = [
    ROLES.SUPER_ADMIN,
    ROLES.MANAGER,
    ROLES.HR,
    ROLES.OFFICE_EMPLOYEE,
    ROLES.SUB_ADMIN,
    ROLES.DEALER,
    ROLES.USER,
    ROLES.CLIENT,
  ].includes(currentUserRole);
  const pagedDrivers = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return filteredDrivers.slice(start, start + rowsPerPage);
  }, [filteredDrivers, page, rowsPerPage]);

  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  useEffect(() => {
    if (!open) return;
    if (mode !== "edit") return;
    if (!editingDriver?.raw) return;
    const d = editingDriver.raw;
    setForm({
      username: String(d?.username || ""),
      uniqueId: String(d?.uniqueId || ""),
      mobileNo: String(d?.mobileNo || ""),
      password: "",
      address: String(d?.address || ""),
      licenseNo: String(d?.licenseNo || ""),
      licenseValidity: toYmd(d?.licenseValidity),
      salary: d?.salary != null ? String(d.salary) : "",
    });
  }, [editingDriver, mode, open]);

  useEffect(() => {
    setShowPassword(false);
  }, [mode, editingDriver?.id, open, entityType]);

  useEffect(() => {
    setWizardStep(0);
    setShowStepErrors(false);
  }, [mode, editingDriver?.id, open, entityType]);

  const clearRevealedPasswords = () => {
    setRevealedPasswords({});
    setLoadingPasswordIds(new Set());
  };

  const handleCloseModal = () => {
    clearRevealedPasswords();
    onClose?.();
  };

  useEffect(() => {
    if (!open) {
      clearRevealedPasswords();
    }
  }, [open]);

  const resetForm = () => {
    setForm({
      username: "",
      uniqueId: "",
      mobileNo: "",
      password: "",
      address: "",
      licenseNo: "",
      licenseValidity: "",
      salary: "",
    });
  };

  const closeForm = () => {
    resetForm();
    setEditingDriver(null);
    setMode("table");
  };

  const handleTogglePassword = async (userId) => {
    const targetId = String(userId || "");
    if (!targetId || !canRevealPasswords) return;

    if (Object.prototype.hasOwnProperty.call(revealedPasswords, targetId)) {
      setRevealedPasswords((prev) => {
        const next = { ...prev };
        delete next[targetId];
        return next;
      });
      return;
    }

    setLoadingPasswordIds((prev) => new Set(prev).add(targetId));
    try {
      const response = await revealUserPassword(targetId).unwrap();
      setRevealedPasswords((prev) => ({
        ...prev,
        [targetId]: response?.password || "",
      }));
    } catch (error) {
      showStatus("error", error?.data?.msg || "Failed to reveal password.");
    } finally {
      setLoadingPasswordIds((prev) => {
        const next = new Set(prev);
        next.delete(targetId);
        return next;
      });
    }
  };

  const renderPasswordCell = (user) => {
    const userId = String(user?._id || user?.id || "");
    const loading = loadingPasswordIds.has(userId);
    const revealedPassword = revealedPasswords[userId];
    const isVisible = Object.prototype.hasOwnProperty.call(revealedPasswords, userId);

    if (!canRevealPasswords) {
      return <VisibilityOffIcon sx={{ fontSize: 18, color: "#9ca3af" }} />;
    }

    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography sx={{ fontWeight: 700, minWidth: 74 }}>
          {isVisible ? revealedPassword : "••••••••"}
        </Typography>
        <IconButton
          type="button"
          size="small"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            handleTogglePassword(userId);
          }}
          disabled={loading}
          sx={{ color: "#2563eb" }}
        >
          {loading ? (
            <CircularProgress size={16} />
          ) : isVisible ? (
            <VisibilityIcon sx={{ fontSize: 18 }} />
          ) : (
            <VisibilityOffIcon sx={{ fontSize: 18 }} />
          )}
        </IconButton>
      </Stack>
    );
  };

  const handleDownloadCsv = () => {
    const csv = isViewerMode
      ? [
          ["Id", "Viewer Name", "Mobile", "Email", "Unique ID"].join(","),
          ...filteredDrivers.map((row) =>
            [row.idLabel, row.username, row.mobileNo, row.email, row.uniqueId]
              .map((value) => `"${String(value ?? "").replace(/"/g, '""')}"`)
              .join(",")
          ),
        ].join("\n")
      : buildCsv(filteredDrivers);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = isViewerMode ? "viewers.csv" : "drivers.csv";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
    showStatus("success", `${entityLabelPlural} CSV downloaded.`);
  };

  const wizardSteps = isViewerMode
    ? [
        { key: "basic", title: "Basic Details", description: "Viewer account information" },
        { key: "permissions", title: "Review", description: "Confirm before save" },
      ]
    : [
        { key: "basic", title: "Basic Details", description: "Driver account information" },
        { key: "preferences", title: "Driver Details", description: "License and address" },
        { key: "permissions", title: "Review", description: "Confirm before save" },
      ];

  const getWizardErrors = () => {
    const errors = {};
    if (!form.username.trim()) errors.username = `${entityLabel} name is required`;
    if (!form.mobileNo.trim()) errors.mobileNo = "Mobile is required";
    if (!isViewerMode && !form.uniqueId.trim()) errors.uniqueId = "Unique ID is required";
    if (mode === "create" && !form.password.trim()) errors.password = "Password is required";
    return errors;
  };

  const wizardErrors = getWizardErrors();
  const visibleWizardErrors = showStepErrors && wizardStep === 0 ? wizardErrors : {};

  const wizardBasicFields = [
    { path: "username", label: `${entityLabel} Name`, required: true },
    { path: "uniqueId", label: "Unique ID", required: !isViewerMode },
    { path: "mobileNo", label: "Mobile", required: true, inputProps: { inputMode: "numeric" } },
    {
      path: "password",
      label: "Password",
      type: "password",
      required: mode === "create",
      helperText: mode === "edit" ? "Leave blank to keep current password." : "",
      md: 12,
    },
  ];

  const wizardExtraFields = [
    { path: "address", label: "Address", md: 12 },
    { path: "licenseNo", label: "License No" },
    { path: "licenseValidity", label: "Validity", type: "date", inputLabelProps: { shrink: true } },
    { path: "salary", label: "Salary", type: "number" },
  ];

  const handleWizardNext = () => {
    if (wizardStep === 0 && Object.keys(wizardErrors).length) {
      setShowStepErrors(true);
      return;
    }
    setShowStepErrors(false);
    setWizardStep((prev) => Math.min(prev + 1, wizardSteps.length - 1));
  };

  const handleSubmit = async () => {
    if (Object.keys(wizardErrors).length) {
      setWizardStep(0);
      setShowStepErrors(true);
      showStatus("warning", isViewerMode ? "Viewer name, mobile, and required fields are needed." : "Driver name, unique ID, mobile, and required fields are needed.");
      return;
    }

    const payload = {
      username: form.username.trim(),
      uniqueId: form.uniqueId.trim() || undefined,
      mobileNo: form.mobileNo.trim(),
      ...(isViewerMode
        ? {}
        : {
            address: form.address,
            licenseNo: form.licenseNo,
            licenseValidity: form.licenseValidity || null,
            salary: form.salary,
          }),
    };

    try {
      if (mode === "edit") {
        const userId = editingDriver?.id;
        if (!userId) throw new Error("Driver id missing");
        await editUser({ userId, ...payload }).unwrap();
        if (form.password.trim()) {
          await resetPassword({ userId, password: form.password.trim() }).unwrap();
        }
        showStatus("success", `${entityLabel} updated.`);
      } else {
        if (!form.password.trim()) {
          showStatus("warning", "Password required hai.");
          return;
        }

        await createUser({
          ...payload,
          email: `${form.mobileNo.trim()}-${Date.now()}@${isViewerMode ? "viewer" : "driver"}.com`,
          password: form.password.trim(),
          role: isViewerMode ? ROLES.VIEWER : ROLES.DRIVER,
        }).unwrap();
        showStatus("success", `${entityLabel} added.`);
      }

      await refetch();
      closeForm();
      setPage(1);
    } catch (error) {
      showStatus("error", error?.data?.msg || error?.data?.message || `${entityLabel} save failed.`);
    }
  };

  const handleDelete = async (row) => {
    const userId = row?.id;
    if (!userId) return;
    if (!window.confirm(`${entityLabel} delete karna hai?`)) return;
    try {
      await deleteUser(userId).unwrap();
      showStatus("success", `${entityLabel} deleted.`);
      await refetch();
    } catch (error) {
      showStatus("error", error?.data?.msg || `Failed to delete ${entityLabel.toLowerCase()}.`);
    }
  };

  const handleLoginAsViewer = async (targetUserId) => {
    if (!targetUserId) return;
    try {
      const currentAdminToken = localStorage.getItem("token");
      if (currentAdminToken) localStorage.setItem("adminFallbackToken", currentAdminToken);

      const response = await impersonateUser(targetUserId).unwrap();
      dispatch(adminApi.util.resetApiState());
      dispatch(loginSuccess({ user: response.user, token: response.token }));
      onClose?.();
      navigate("/dashboard", { replace: true, state: { __impersonate: true, t: Date.now() } });
    } catch (error) {
      showStatus("error", error?.data?.msg || "Failed to login as viewer.");
    }
  };

  const resetAssignDeviceDialog = () => {
    setAssignDeviceDialogUser(null);
    setAssignDeviceSearch("");
    setPendingAssignIds(new Set());
    setAssignOverrides(new Map());
    setAssignDeviceCache(new Map());
  };

  const handleToggleAssignViewerDevice = async (deviceRow, nextChecked) => {
    const viewerId = String(assignDeviceDialogUser?.id || assignDeviceDialogUser?._id || "");
    if (!viewerId || !deviceRow?.id) return;

    const deviceId = String(deviceRow.id);
    const nextAssignedViewers = nextChecked
      ? Array.from(new Set([...(deviceRow.assignedToViewers || []), viewerId]))
      : (deviceRow.assignedToViewers || []).filter((id) => String(id) !== viewerId);

    setPendingAssignIds((prev) => new Set(prev).add(deviceId));
    try {
      await editDevice({
        deviceId,
        assignedToViewers: nextAssignedViewers,
      }).unwrap();

      setAssignDeviceCache((prev) => {
        const next = new Map(prev);
        const existing = next.get(deviceId) || {};
        next.set(deviceId, {
          ...existing,
          _id: existing?._id || deviceId,
          assignedToViewers: nextAssignedViewers,
        });
        return next;
      });
      setAssignOverrides((prev) => {
        const next = new Map(prev);
        next.set(deviceId, Boolean(nextChecked));
        return next;
      });
      showStatus("success", nextChecked ? "Viewer assigned successfully." : "Viewer unassigned successfully.");
    } catch (error) {
      showStatus("error", error?.data?.msg || "Failed to update viewer device assignment.");
    } finally {
      setPendingAssignIds((prev) => {
        const next = new Set(prev);
        next.delete(deviceId);
        return next;
      });
    }
  };

  const busy = isCreating || isEditing || isDeleting || isResetting;

  if (mode !== "table") {
    return (
      <WizardModalLayout
        open={open}
        onClose={handleCloseModal}
        icon={wizardStep === wizardSteps.length - 1 ? PreviewOutlinedIcon : DriveEtaOutlinedIcon}
        title={mode === "edit" ? `Edit ${entityLabel}` : `Add ${entityLabel}`}
        subtitle={
          mode === "edit"
            ? `Update ${entityLabel.toLowerCase()} information in guided steps.`
            : `Create a new ${entityLabel.toLowerCase()} account in guided steps.`
        }
        steps={wizardSteps}
        currentStep={wizardStep}
        onStepChange={setWizardStep}
        helpTitle="Need Help?"
        helpText={
          isViewerMode
            ? "Fill the basic details first, then review everything before saving. Existing viewer create and edit APIs remain unchanged."
            : "Complete the required account details, then add driver information before saving. Existing driver create and edit APIs remain unchanged."
        }
        fullScreen={fullScreen}
        maxWidth="lg"
        footer={
          <StickyModalFooter
            currentStep={wizardStep}
            steps={wizardSteps}
            loading={busy}
            onBack={() => setWizardStep((prev) => Math.max(prev - 1, 0))}
            onCancel={closeForm}
            onNext={handleWizardNext}
            onSubmit={handleSubmit}
            submitLabel={mode === "edit" ? `Save ${entityLabel}` : `Create ${entityLabel}`}
          />
        }
      >
        {wizardStep === 0 ? (
          <BasicDetailsStep
            title={`${entityLabel} Information`}
            description={`Enter the required ${entityLabel.toLowerCase()} account details.`}
            form={form}
            onChange={(path, value) =>
              setForm((prev) => ({
                ...prev,
                [path]: path === "mobileNo" ? value.replace(/\D/g, "") : value,
              }))
            }
            fields={wizardBasicFields}
            errors={visibleWizardErrors}
            tip={
              mode === "edit"
                ? "Leaving the password blank keeps the current password unchanged."
                : "These fields still submit through the same existing create API."
            }
          />
        ) : null}

        {!isViewerMode && wizardStep === 1 ? (
          <BasicDetailsStep
            title="Driver Details"
            description="Add address, license, and salary information if available."
            form={form}
            onChange={(path, value) =>
              setForm((prev) => ({
                ...prev,
                [path]: value,
              }))
            }
            fields={wizardExtraFields}
            errors={{}}
          />
        ) : null}

        {wizardStep === wizardSteps.length - 1 ? (
          <Paper
            sx={{
              borderRadius: 4,
              border: "1px solid #dbe4f0",
              backgroundColor: "#fff",
              p: { xs: 2, sm: 2.5 },
              boxShadow: "0 20px 40px rgba(15, 23, 42, 0.05)",
            }}
          >
            <Grid container spacing={2}>
              {[
                [`${entityLabel} Name`, form.username || "-"],
                ["Unique ID", form.uniqueId || "-"],
                ["Mobile", form.mobileNo || "-"],
                ...(!isViewerMode
                  ? [
                      ["Address", form.address || "-"],
                      ["License No", form.licenseNo || "-"],
                      ["Validity", form.licenseValidity || "-"],
                      ["Salary", form.salary || "-"],
                    ]
                  : [["Email Pattern", `${form.mobileNo || "mobile"}@viewer.com`]]),
              ].map(([label, value]) => (
                <Grid item xs={12} md={6} key={label}>
                  <Box
                    sx={{
                      borderRadius: 3,
                      border: "1px solid #e5e7eb",
                      backgroundColor: "#fff",
                      p: 1.6,
                    }}
                  >
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#64748b" }}>{label}</Typography>
                    <Typography sx={{ mt: 0.5, fontSize: 16, fontWeight: 800, color: "#0f172a" }}>{value}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        ) : null}
      </WizardModalLayout>
    );
  }

  return (
    <>
      <Dialog open={open} onClose={handleCloseModal} fullWidth maxWidth="xl" PaperProps={{ sx: { borderRadius: 2, overflow: "hidden", minHeight: "76vh" } }}>
        <DialogContent sx={{ p: 0, bgcolor: "#fff" }}>
          <Box sx={{ borderTop: "4px solid #4f9cf9", display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: 2 }}>
            <Typography sx={{ fontSize: 20, fontWeight: 800 }}>
              {mode === "table" ? entityLabel : `Add ${entityLabel}`}
            </Typography>
            <IconButton onClick={handleCloseModal}>
              <CloseIcon />
            </IconButton>
          </Box>

          {mode === "table" ? (
            <>
              <Box sx={{ px: 3, py: 2.5, borderTop: "1px solid #e5e7eb", borderBottom: "1px solid #e5e7eb" }}>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "360px auto" }, gap: 1.5, alignItems: "center" }}>
                <TextField
                  placeholder="Search"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  sx={inputSx}
                  InputProps={{ endAdornment: <SearchIcon style={{ color: "#fb7185" }} /> }}
                />
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <IconButton onClick={() => { setMode("create"); setEditingDriver(null); resetForm(); }} sx={{ border: "2px solid #111827", borderRadius: 0.5, color: "#111827" }}>
                    <AddIcon />
                  </IconButton>
                </Stack>
              </Box>
            </Box>

            <Box sx={{ px: 3, py: 1.5, borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <Select value={rowsPerPage} onChange={(event) => { setRowsPerPage(Number(event.target.value)); setPage(1); }}>
                  {[10, 25, 50, 100].map((size) => (
                    <MenuItem key={size} value={size}>
                      {size}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Stack direction="row" spacing={1}>
                <IconButton onClick={() => refetch()} sx={{ color: "#6b7280" }}>
                  <RefreshIcon />
                </IconButton>
                <IconButton onClick={handleDownloadCsv} sx={{ color: "#6b7280" }}>
                  <DownloadIcon />
                </IconButton>
              </Stack>
            </Box>

            <TableContainer component={Paper} elevation={0} sx={{ maxHeight: "52vh", borderRadius: 0 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {(isViewerMode
                      ? ["Id", "Viewer Name", "Password", "Mobile", "Email", "Unique ID", "Action"]
                      : ["Id", "Driver Name", "Password", "Unique ID", "Mobile", "Address", "License no", "License Validity", "Salary", "Action"]
                    ).map((head) => (
                      <TableCell key={head} sx={{ fontWeight: 700, color: "#6b7280", bgcolor: "#fff", borderBottom: "1px solid #d1d5db" }}>
                        {head}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedDrivers.length ? (
                    pagedDrivers.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell sx={{ fontWeight: 700 }}>{row.idLabel}</TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>{row.username}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{renderPasswordCell(row.raw)}</TableCell>
                        {isViewerMode ? (
                          <>
                            <TableCell sx={{ fontWeight: 700 }}>{row.mobileNo}</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>{row.email}</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>{row.uniqueId}</TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell sx={{ fontWeight: 700 }}>{row.uniqueId}</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>{row.mobileNo}</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>{row.address}</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>{row.licenseNo}</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>{row.licenseValidityLabel}</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>{row.salary}</TableCell>
                          </>
                        )}
                        <TableCell>
                          <Stack direction="row" spacing={1}>
                            {isViewerMode && !row.raw?.isDeleted && (
                              <Tooltip title="Login as Viewer">
                                <IconButton
                                  size="small"
                                  onClick={() => handleLoginAsViewer(row.id)}
                                  sx={{ bgcolor: "#f8fafc", borderRadius: 1 }}
                                >
                                  <LoginIcon sx={{ fontSize: 18, color: "#111827" }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {isViewerMode && !row.raw?.isDeleted && (
                              <Tooltip title="Assign Device">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setAssignDeviceDialogUser(row);
                                    setAssignDeviceSearch("");
                                    setPendingAssignIds(new Set());
                                    setAssignOverrides(new Map());
                                  }}
                                  sx={{ bgcolor: "#2563eb", borderRadius: 1 }}
                                >
                                  <DirectionsCarFilledOutlinedIcon sx={{ color: "#fff", fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title={`Edit ${entityLabel}`}>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setEditingDriver(row);
                                  setMode("edit");
                                }}
                                sx={{ bgcolor: "#2563eb", borderRadius: 1 }}
                              >
                                <EditOutlinedIcon sx={{ color: "#fff", fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={`Delete ${entityLabel}`}>
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(row)}
                                sx={{ bgcolor: "#ef4444", borderRadius: 1 }}
                                disabled={busy}
                              >
                                <DeleteOutlineIcon sx={{ color: "#fff", fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={isViewerMode ? 7 : 10}>
                        <EmptyState />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ px: 3, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography sx={{ color: "#374151" }}>
                Showing {filteredDrivers.length ? (page - 1) * rowsPerPage + 1 : 0} to {Math.min(page * rowsPerPage, filteredDrivers.length)} of {filteredDrivers.length} entries
              </Typography>
              <Pagination page={page} count={totalPages} onChange={(_, value) => setPage(value)} shape="rounded" />
            </Box>
          </>
        ) : (
          <Box sx={{ borderTop: "1px solid #e5e7eb" }}>
            <Box sx={{ px: 3, py: 2.5 }}>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 2.5 }}>
                <Box>
                  <Typography sx={{ mb: 1, fontSize: 18, fontWeight: 500 }}>{entityLabel} Name *</Typography>
                  <TextField
                    fullWidth
                    value={form.username}
                    onChange={(event) => setForm((prev) => ({ ...prev, username: event.target.value }))}
                    sx={inputSx}
                  />
                </Box>
                <Box>
                  <Typography sx={{ mb: 1, fontSize: 18, fontWeight: 500 }}>Unique Id{isViewerMode ? "" : " *"}</Typography>
                  <TextField
                    fullWidth
                    value={form.uniqueId}
                    onChange={(event) => setForm((prev) => ({ ...prev, uniqueId: event.target.value }))}
                    sx={inputSx}
                  />
                </Box>
                <Box>
                  <Typography sx={{ mb: 1, fontSize: 18, fontWeight: 500 }}>Mobile *</Typography>
                  <TextField
                    fullWidth
                    value={form.mobileNo}
                    onChange={(event) => setForm((prev) => ({ ...prev, mobileNo: event.target.value }))}
                    sx={inputSx}
                  />
                </Box>
                <Box sx={{ gridColumn: { xs: "1", md: "1 / span 3" }, maxWidth: 520 }}>
                  <Typography sx={{ mb: 1, fontSize: 18, fontWeight: 500 }}>Password{mode === "create" ? " *" : ""}</Typography>
                  <TextField
                    fullWidth
                    type={showPassword ? "text" : "password"}
                    value={form.password}
                    onChange={(event) => setForm((prev) => ({ ...prev, password: event.target.value }))}
                    sx={inputSx}
                    placeholder={mode === "edit" ? "Leave blank to keep unchanged" : ""}
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          edge="end"
                          onClick={() => setShowPassword((prev) => !prev)}
                          aria-label={showPassword ? "Hide password" : "Show password"}
                          sx={{ mr: 0.25 }}
                        >
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      ),
                    }}
                  />
                </Box>
              </Box>

              {!isViewerMode && (
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 2.5 }}>
                    <Box>
                      <Typography sx={{ mb: 1, fontSize: 18, fontWeight: 500 }}>Address</Typography>
                      <TextField
                        fullWidth
                        value={form.address}
                        onChange={(event) => setForm((prev) => ({ ...prev, address: event.target.value }))}
                        sx={inputSx}
                      />
                    </Box>
                    <Box>
                      <Typography sx={{ mb: 1, fontSize: 18, fontWeight: 500 }}>License no</Typography>
                      <TextField
                        fullWidth
                        value={form.licenseNo}
                        onChange={(event) => setForm((prev) => ({ ...prev, licenseNo: event.target.value }))}
                        sx={inputSx}
                      />
                    </Box>
                    <Box>
                      <Typography sx={{ mb: 1, fontSize: 18, fontWeight: 500 }}>Validity</Typography>
                      <TextField
                        fullWidth
                        type="date"
                        value={form.licenseValidity}
                        onChange={(event) => setForm((prev) => ({ ...prev, licenseValidity: event.target.value }))}
                        sx={inputSx}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Box>
                    <Box sx={{ maxWidth: 520 }}>
                      <Typography sx={{ mb: 1, fontSize: 18, fontWeight: 500 }}>Salary</Typography>
                      <TextField
                        fullWidth
                        value={form.salary}
                        onChange={(event) => setForm((prev) => ({ ...prev, salary: event.target.value }))}
                        sx={inputSx}
                      />
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>

            <Box sx={{ px: 3, pb: 2.5, display: "flex", gap: 2 }}>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={busy}
                sx={{
                  minWidth: 220,
                  bgcolor: "#f43f5e",
                  "&:hover": { bgcolor: "#e11d48" },
                  fontWeight: 800,
                  borderRadius: 1.2,
                }}
              >
                {busy ? "SUBMIT..." : "SUBMIT"}
              </Button>
              <Button
                variant="contained"
                onClick={closeForm}
                disabled={busy}
                sx={{
                  minWidth: 220,
                  bgcolor: "#6b7280",
                  "&:hover": { bgcolor: "#4b5563" },
                  fontWeight: 800,
                  borderRadius: 1.2,
                }}
              >
                CANCEL
              </Button>
            </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(assignDeviceDialogUser) && isViewerMode}
        onClose={resetAssignDeviceDialog}
        fullWidth
        maxWidth="xl"
        PaperProps={{ sx: { borderRadius: 2, overflow: "hidden", minHeight: "72vh" } }}
      >
        <DialogContent sx={{ p: 0, bgcolor: "#fff" }}>
        <Box sx={{ borderTop: "4px solid #4f9cf9", display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: 2 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 900 }}>Assign Device</Typography>
          <IconButton onClick={resetAssignDeviceDialog}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ px: 3, py: 2 }}>
          <Typography sx={{ color: "#6b7280", fontWeight: 800, fontSize: 15, mb: 1.5 }}>Selected Viewer</Typography>
          <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 0, border: "1px solid #eef2f7" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {["Name", "Mobile", "Email", "Unique ID"].map((head) => (
                    <TableCell key={head} sx={{ fontWeight: 700, color: "#6b7280", bgcolor: "#f8fafc", whiteSpace: "nowrap" }}>
                      {head}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>{assignDeviceDialogUser?.username || "-"}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{assignDeviceDialogUser?.mobileNo || "-"}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{assignDeviceDialogUser?.email || "-"}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{assignDeviceDialogUser?.uniqueId || "-"}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

          <Box sx={{ px: 3, pb: 1.5 }}>
            <TextField
              placeholder="Search"
              value={assignDeviceSearch}
              onChange={(event) => setAssignDeviceSearch(event.target.value)}
              sx={{ ...inputSx, width: "100%" }}
              InputProps={{ endAdornment: <SearchIcon style={{ color: "#fb7185" }} /> }}
            />
          </Box>

          <Box sx={{ px: 3, pb: 1.5 }}>
            <Box sx={{ fontSize: 12, color: "#475569", bgcolor: "#f8fafc", border: "1px dashed #cbd5e1", borderRadius: 1.5, px: 1.5, py: 1 }}>
              {`DEBUG lite=${assignDeviceDebug.liteCount} full=${assignDeviceDebug.fullCount} my=${assignDeviceDebug.myDevicesCount} safe=${assignDeviceDebug.safeCount} cache=${assignDeviceDebug.cacheCount} visible=${assignDeviceDebug.visibleCount} query="${assignDeviceDebug.query}" user=${assignDeviceDebug.targetUserId}`}
            </Box>
          </Box>

          <Box sx={{ px: 3, pb: 2.5 }}>
          <TableContainer component={Paper} elevation={0} sx={{ maxHeight: "46vh", borderRadius: 0, border: "1px solid #eef2f7" }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 48, bgcolor: "#fff" }} />
                  {["Vehicle No", "IMEI", "Status", "Last Update", "Sim No", "Model", "Icon"].map((head) => (
                    <TableCell key={head} sx={{ fontWeight: 800, color: "#111827", bgcolor: "#fff", whiteSpace: "nowrap" }}>
                      {head}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {assignableDevices.length ? (
                  assignableDevices.map((device) => {
                    const pending = pendingAssignIds.has(String(device.id));
                    return (
                      <TableRow key={device.id} hover>
                        <TableCell>
                          <input
                            type="checkbox"
                            checked={device.checked}
                            disabled={pending}
                            onChange={(event) => handleToggleAssignViewerDevice(device, event.target.checked)}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{device.name}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{device.imei}</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: String(device.status || "").toLowerCase().includes("stop") ? "#10b981" : String(device.status || "").toLowerCase().includes("running") ? "#10b981" : "#ef4444" }}>
                          {String(device.status || "").replace(/_/g, " ")}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{device.lastUpdate}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{device.simNo}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{device.model}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{device.icon}</TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6, color: "#6b7280" }}>
                      No devices found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button
              variant="contained"
              onClick={resetAssignDeviceDialog}
              sx={{
                minWidth: 220,
                bgcolor: "#6b7280",
                "&:hover": { bgcolor: "#4b5563" },
                fontWeight: 900,
                borderRadius: 1.2,
              }}
            >
              Close
            </Button>
          </Stack>
        </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
